import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMsCancelAssetRequestMutation } from "@/services/(my-space)/api.assets";
import { toast } from "react-hot-toast";
import { TextField } from "@/components/Textfield";
import * as yup from "yup";

interface IProps {
  open: boolean;
  onClose: () => void;
  requestId: number | null;
}

const CancelAssetRequestDialog = ({ open, onClose, requestId }: IProps) => {
  const { t } = useTranslation(["my-space", "assets", "common", "validation"]);

  const [cancelAssetRequest, { isLoading: isCancellingAssetReq }] =
    useMsCancelAssetRequestMutation();

  const formik = useFormik<{ cancellationReason: string }>({
    initialValues: { cancellationReason: "" },
    validationSchema: yup.object({
      cancellationReason: yup
        .string()
        .required(
          t("validation:required_field", {
            field: t("assets:my_space.cancel_reason_label"),
          }),
        )
        .min(3, t("validation:min_length", { count: 3 }))
        .max(200, t("validation:max_length", { count: 200 })),
    }),
    onSubmit: async (formData) => {
      if (!requestId) return;
      const id = toast.loading(t("common:states.processing"));
      await cancelAssetRequest({
        assetRequestId: requestId,
        reason: formData.cancellationReason,
      })
        .unwrap()
        .then((res) => {
          toast.success(res.message, { id });
          onClose();
        })
        .catch((e) =>
          toast.error(e?.data?.errors?.message ?? e?.data?.message, { id }),
        );
    },
  });

  // formik.resetForm is stable — safe to omit from deps
  const resetForm = formik.resetForm;
  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("assets:my_space.cancel_request")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "ui.are_you_sure_you_want_to_cancel_this_asset_request_this_acti",
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={formik.handleSubmit} className="mb-4">
          <TextField
            type="text"
            formik={formik}
            id="cancellationReason"
            name="cancellationReason"
            placeholder={t("assets:my_space.cancel_reason_placeholder")}
            label={t("assets:my_space.cancel_reason_label")}
          />
        </form>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={onClose}
            type="reset"
            disabled={isCancellingAssetReq}
          >
            {t("ui.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            type="submit"
            onClick={formik.submitForm}
            disabled={isCancellingAssetReq}
            className="text-white bg-danger"
          >
            {t("ui.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelAssetRequestDialog;
